/**
 * The online race's screens, two of the game's list menus: one to host a race or join one with a
 * code, one for the room, with the code to share, who is in, and the way on (the host picks a
 * course; a guest waits). The room's state lives in the session (`session.ts`); these are its face.
 */

import { EXPLICIT_NAME_ERROR, isExplicitName, normalizePlayerName } from "@lache/relay/names";
import { CODE_LENGTH, cleanCode, validCode } from "@lache/relay/room";
import { HEADBANDS, headband } from "@lache/render";
import { ListMenu, type MenuItem } from "../menu";
import { cleanName, MAX_NAME_LENGTH } from "../scores";
import type { OnlineSession, PeerInfo } from "./session";

export interface OnlineScreenHost {
  session: OnlineSession;
  getName(): string;
  setName(name: string): void;
  getBand(): number;
  setBand(band: number): void;
  /** Host a room, join one, leave the room, leave the screen, or (the host) go pick a course. */
  onHost(): void;
  onJoin(code: string): void;
  onLeave(): void;
  onBack(): void;
  onPick(): void;
  /** Put one of the two menus up (the app hides the rest and keeps the backdrop). */
  present(menu: ListMenu): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text = "",
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = className;
  if (text) e.textContent = text;
  return e;
}

function cssColor(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

/** The link a friend opens to join: the game's address with the code. */
export function roomLink(code: string): string {
  const url = new URL(location.href);
  url.search = "";
  url.hash = "";
  url.searchParams.set("room", code);
  return url.toString();
}

export class OnlineScreen {
  /** Host or join. */
  readonly menu: ListMenu;
  /** In a room. */
  readonly room: ListMenu;
  private readonly nameInput: HTMLInputElement;
  private readonly codeInput: HTMLInputElement;
  private readonly swatches: HTMLButtonElement[] = [];
  private readonly menuStatus: HTMLDivElement;
  private readonly codeEl: HTMLDivElement;
  private readonly linkEl: HTMLDivElement;
  private readonly shareButton: HTMLButtonElement;
  private readonly copyButton: HTMLButtonElement;
  /** Opens the phone's messages with the link typed in (`sms:`), shown in touch mode. */
  private readonly textLink: HTMLAnchorElement;
  private readonly peersEl: HTMLDivElement;
  private readonly roomStatus: HTMLDivElement;
  private note = "";
  private noteUntil = 0;
  private nameError = "";

  constructor(
    parent: HTMLElement,
    private readonly host: OnlineScreenHost,
  ) {
    this.menu = new ListMenu(parent, {
      className: "online-menu online",
      title: "Multiplayer",
      hint: "stick or arrows to choose, Jump or Enter to go, Escape to go back",
      touchHint: "tap to choose",
      back: "Back",
      onCancel: () => this.host.onBack(),
    });
    this.room = new ListMenu(parent, {
      className: "online-menu online room",
      title: "Multiplayer",
      hint: "Jump or Enter to go, Escape to leave the race",
      touchHint: "tap to choose",
      back: "Leave",
      onCancel: () => this.host.onLeave(),
    });

    // Who you are: the name (the scoreboard's) and a friend's code on the left, the headband on the right.
    const you = el("div", "online-you");
    const fields = el("div", "online-fields");
    this.nameInput = document.createElement("input");
    this.nameInput.type = "text";
    this.nameInput.maxLength = MAX_NAME_LENGTH;
    this.nameInput.spellcheck = false;
    this.nameInput.setAttribute("aria-label", "your name");
    this.nameInput.setAttribute("aria-describedby", "multiplayer-name-status");
    this.nameInput.addEventListener("input", () => this.clearNameError());
    this.nameInput.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.code === "Enter" || e.code === "Escape") {
        e.preventDefault();
        this.nameInput.blur();
      }
    });
    this.nameInput.addEventListener("keyup", (e) => e.stopPropagation());
    this.nameInput.addEventListener("change", () => this.commitName());
    this.nameInput.addEventListener("blur", () => this.commitName());
    const swatchRow = el("div", "seat-swatches");
    HEADBANDS.forEach((b, k) => {
      const s = document.createElement("button");
      s.type = "button";
      s.className = "swatch";
      s.title = b.name;
      s.style.setProperty("--swatch", cssColor(b.color));
      s.addEventListener("click", (e) => {
        if (e.defaultPrevented) return;
        this.host.setBand(k);
        this.render();
      });
      swatchRow.appendChild(s);
      this.swatches.push(s);
    });
    // The code a friend gave you.
    const join = el("div", "online-join");
    this.codeInput = document.createElement("input");
    this.codeInput.type = "text";
    this.codeInput.className = "online-code-input";
    this.codeInput.maxLength = CODE_LENGTH;
    this.codeInput.placeholder = "CODE";
    this.codeInput.autocomplete = "off";
    this.codeInput.autocapitalize = "characters";
    this.codeInput.spellcheck = false;
    this.codeInput.setAttribute("aria-label", "room code");
    this.codeInput.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.code === "Enter") {
        e.preventDefault();
        this.join();
      } else if (e.code === "Escape") {
        e.preventDefault();
        this.codeInput.blur();
      }
    });
    this.codeInput.addEventListener("keyup", (e) => e.stopPropagation());
    this.codeInput.addEventListener("input", () => {
      const clean = cleanCode(this.codeInput.value);
      if (clean !== this.codeInput.value) this.codeInput.value = clean;
    });
    join.append(el("span", "online-join-label", "a friend's code"), this.codeInput);
    fields.append(this.nameInput, join);
    you.append(fields, swatchRow);
    this.menuStatus = el("div", "lobby-status online-status");
    this.menuStatus.id = "multiplayer-name-status";
    this.menuStatus.setAttribute("role", "status");
    this.menu.content.append(you, this.menuStatus);

    // The room: the code, the link, who is in, one line on what happens next.
    const codeBox = el("div", "online-code-box");
    codeBox.append(el("div", "online-code-label", "room code"));
    this.codeEl = el("div", "online-code");
    codeBox.appendChild(this.codeEl);
    // The link, with Copy beside it and Share where the browser has a share sheet (phones).
    const linkRow = el("div", "online-link-row");
    this.linkEl = el("div", "online-link");
    this.copyButton = document.createElement("button");
    this.copyButton.type = "button";
    this.copyButton.className = "menu-back online-copy";
    this.copyButton.textContent = "Copy";
    this.copyButton.addEventListener("click", (e) => {
      if (!e.defaultPrevented) this.copy();
    });
    this.textLink = document.createElement("a");
    this.textLink.className = "menu-back online-text";
    this.textLink.textContent = "Text";
    this.textLink.rel = "noopener";
    this.shareButton = document.createElement("button");
    this.shareButton.type = "button";
    this.shareButton.className = "menu-back online-share";
    this.shareButton.textContent = "Share";
    this.shareButton.addEventListener("click", (e) => {
      if (!e.defaultPrevented) this.share();
    });
    linkRow.append(this.linkEl, this.copyButton, this.textLink, this.shareButton);
    codeBox.appendChild(linkRow);
    // An iPhone opens a tapped link in Safari, never in the home-screen app: the code is the way over.
    codeBox.appendChild(
      el("div", "online-tip", "For fullscreen, open Lache from your home screen and type this code."),
    );
    this.peersEl = el("div", "online-peers");
    this.roomStatus = el("div", "lobby-status online-status");
    this.room.content.append(codeBox, this.peersEl, this.roomStatus);
  }

  get open(): boolean {
    return this.menu.open || this.room.open;
  }

  private clearNameError(): void {
    this.nameError = "";
    this.nameInput.removeAttribute("aria-invalid");
    this.updateMenuStatus();
  }

  private commitName(): boolean {
    const explicit = isExplicitName(this.nameInput.value);
    if (explicit || !normalizePlayerName(this.nameInput.value)) {
      this.nameError = explicit ? EXPLICIT_NAME_ERROR : "Enter a username.";
      this.nameInput.setAttribute("aria-invalid", "true");
      // Updating the message must not replace a button while a blur precedes its click.
      this.updateMenuStatus();
      return false;
    }
    this.clearNameError();
    const name = cleanName(this.nameInput.value);
    this.nameInput.value = name;
    this.host.setName(name);
    return true;
  }

  private join(): void {
    if (!this.commitName()) return;
    const code = cleanCode(this.codeInput.value);
    if (!validCode(code)) {
      this.say(`a code is ${CODE_LENGTH} letters or digits`);
      this.codeInput.focus();
      return;
    }
    this.codeInput.blur();
    this.host.onJoin(code);
  }

  /** Put a code in the join field (the ?room= link). */
  setCode(code: string): void {
    this.codeInput.value = cleanCode(code);
  }

  private static canShare(): boolean {
    return typeof (navigator as Navigator & { share?: unknown }).share === "function";
  }

  private share(): void {
    const code = this.host.session.code;
    const link = roomLink(code);
    const nav = navigator as Navigator & {
      share?: (data: { title: string; text: string; url: string }) => Promise<void>;
    };
    if (typeof nav.share === "function") {
      nav.share({ title: "Lache", text: `Race me on Lache: room ${code}`, url: link }).catch(() => {
        // Cancelled, or not allowed here: the link stays on screen to copy.
      });
      return;
    }
    this.copy();
  }

  /** The link onto the clipboard; where the clipboard API is refused, the older copy command, quietly. */
  private copy(): void {
    const link = roomLink(this.host.session.code);
    const fallback = (): void => {
      const range = document.createRange();
      range.selectNodeContents(this.linkEl);
      const selection = getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      let copied = false;
      try {
        copied = document.execCommand("copy");
      } catch {
        copied = false;
      }
      selection?.removeAllRanges();
      if (copied) this.say("link copied");
    };
    if (!navigator.clipboard) {
      fallback();
      return;
    }
    navigator.clipboard
      .writeText(link)
      .then(() => this.say("link copied"))
      .catch(fallback);
  }

  /** A short note on the status line. */
  say(text: string, ms = 2500): void {
    this.note = text;
    this.noteUntil = performance.now() + ms;
    this.render();
  }

  /** Open (or re-open) the screen for the session's state. */
  show(): void {
    this.nameInput.value = this.host.getName();
    this.clearNameError();
    this.render(true);
  }

  hide(): void {
    this.menu.hide();
    this.room.hide();
  }

  private menuItems(): MenuItem[] {
    return [
      {
        label: "Host a race",
        action: () => {
          if (this.commitName()) this.host.onHost();
        },
      },
      {
        label: "Join a race",
        action: () => {
          if (validCode(cleanCode(this.codeInput.value))) this.join();
          else this.codeInput.focus();
        },
      },
    ];
  }

  private roomItems(): MenuItem[] {
    const s = this.host.session;
    const items: MenuItem[] = [];
    if (s.state === "reconnecting") items.push({ label: "Reconnecting…", action: () => {} });
    else if (s.isHost) items.push({ label: "Pick a course", action: () => this.host.onPick() });
    else items.push({ label: "Waiting for the host", action: () => {} });
    items.push({ label: "Leave", action: () => this.host.onLeave() });
    return items;
  }

  private peerChip(p: PeerInfo, extra: string): HTMLSpanElement {
    const chip = el("span", "online-peer");
    chip.classList.toggle("left", p.left || p.away);
    const dot = el("span", "seat-chip", `P${p.peer + 1}`);
    dot.style.setProperty("--band", cssColor(headband(p.band).color));
    chip.append(dot, ` ${p.name}${extra}`);
    return chip;
  }

  /**
   * Redraw for the session's state. `present` brings the right menu up; otherwise a menu is only
   * switched when one of the two is already showing (a notice during a race changes nothing on screen).
   */
  render(present = false): void {
    const s = this.host.session;
    const inRoom = s.active;
    const showing = this.menu.open || this.room.open;
    const band = this.host.getBand();
    this.swatches.forEach((sw, k) => {
      sw.classList.toggle("selected", k === band);
    });
    const now = performance.now();
    const note = this.note && now < this.noteUntil ? this.note : "";
    if (inRoom) {
      this.codeEl.textContent = s.code;
      const link = roomLink(s.code);
      this.linkEl.textContent = link;
      // `sms:?&body=` opens the messages app with the text ready on both iPhones and Android phones.
      this.textLink.href = `sms:?&body=${encodeURIComponent(`Race me on Lache! Room ${s.code}: ${link}`)}`;
      this.shareButton.hidden = !OnlineScreen.canShare();
      this.peersEl.replaceChildren();
      const me: PeerInfo = {
        peer: s.myPeer,
        name: this.host.getName(),
        band,
        tuningHash: "",
        commit: "",
        known: true,
        left: false,
        away: s.state === "reconnecting",
      };
      // Seat order, the host first, with "you" marked wherever you sit.
      const seats = [...s.others(), me].sort((a, b) => a.peer - b.peer);
      for (const p of seats) {
        const extra =
          p === me ? " (you)" : p.left ? " (left)" : p.away ? " (away)" : p.known ? "" : " (joining)";
        this.peersEl.appendChild(this.peerChip(p, extra));
      }
      const n = s.present().length;
      this.roomStatus.textContent =
        (s.state === "reconnecting" ? "Reconnecting…" : "") ||
        note ||
        (s.peers.get(0)?.away
          ? "Host reconnecting…"
          : s.isHost
            ? n === 0
              ? "share the code, then pick a course"
              : `${n} in · pick a course to start`
            : "the host picks the course");
      this.room.setItems(this.roomItems(), this.room.open ? this.room.selected : 0);
      if (present || (showing && !this.room.open)) this.host.present(this.room);
      return;
    }
    this.updateMenuStatus();
    this.menu.setItems(this.menuItems(), this.menu.open ? this.menu.selected : 0);
    if (present || (showing && !this.menu.open)) this.host.present(this.menu);
  }

  private updateMenuStatus(): void {
    const s = this.host.session;
    const note = this.note && performance.now() < this.noteUntil ? this.note : "";
    this.menuStatus.textContent =
      this.nameError ||
      note ||
      (s.state === "connecting"
        ? s.isHost
          ? "opening a room…"
          : `joining ${s.code}…`
        : s.state === "error"
          ? s.error || "could not connect"
          : "");
    this.menuStatus.classList.toggle("error", this.nameError !== "" || s.state === "error");
  }
}
